import { useCallback, useMemo } from 'react';

import type { NavGroupApi, NavItemApi } from '~/types/navigation.types';

import { useNavigation } from './use-navigation';

/**
 * Recursively collects allowed paths from a single nav node, cascading on
 * presence: a child path is collected only if its parent node was visited.
 * Because the API nests children inside their parent, a disabled parent
 * (omitted by the backend) structurally drops its whole subtree from the
 * allow-list — this function makes that contract explicit so future shape
 * changes (e.g. flat lists with parent ids) won't silently break the cascade.
 */
function collectPaths(node: NavGroupApi | NavItemApi, out: string[]): void {
  if (node.path) out.push(node.path);
  for (const child of node.items) {
    collectPaths(child, out);
  }
}

/**
 * Extracts all allowed navigation paths from the API response and exposes a
 * function to check whether a given pathname is allowed. Used by ScreenGuard
 * to enforce URL-level access in lockstep with the sidebar — both read from
 * the same /screens/navigation tree.
 *
 * A path is allowed when it exactly matches a returned path OR is a nested
 * detail route under one (prefix match on `<allowed>/`). When the backend
 * disables a screen, it omits the node — and the cascade in `collectPaths`
 * guarantees its children disappear with it.
 */
export function useScreenAccess() {
  const { data, isLoading, isError, refetch } = useNavigation();

  const allowedPaths = useMemo(() => {
    if (!data?.groups) return [];
    const paths: string[] = [];
    for (const group of data.groups) {
      collectPaths(group, paths);
    }
    return paths;
  }, [data]);

  const isPathAllowed = useCallback(
    (pathname: string): boolean => {
      if (isError) return false;
      if (!allowedPaths.length) return false;
      return allowedPaths.some(
        (allowed) => pathname === allowed || pathname.startsWith(allowed + '/'),
      );
    },
    [allowedPaths, isError],
  );

  return { isPathAllowed, allowedPaths, isLoading, isError, refetch };
}
